import { Check } from 'lucide-react';
import { cn } from '@/lib/utils';

interface Step {
  label: string;
  shortLabel: string;
}

interface WorkflowStepIndicatorProps {
  currentStep: number;
  steps: Step[];
  onStepClick?: (step: number) => void;
  canNavigateToStep?: (step: number) => boolean;
}

export function WorkflowStepIndicator({ currentStep, steps, onStepClick, canNavigateToStep }: WorkflowStepIndicatorProps) {
  return (
    <div className="flex items-center gap-1 w-full" style={{ pointerEvents: 'auto' }}>
      {steps.map((step, index) => {
        const stepNum = index + 1;
        const isActive = stepNum === currentStep;
        const isComplete = stepNum < currentStep;
        const isClickable = onStepClick && (!canNavigateToStep || canNavigateToStep(stepNum));

        return (
          <div key={stepNum} className="flex items-center flex-1">
            <div className="flex flex-col items-center flex-1">
              <button
                type="button"
                onClick={() => isClickable && onStepClick(stepNum)}
                disabled={!isClickable}
                className={cn(
                  'w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold border-2 transition-colors',
                  isComplete && 'bg-primary border-primary text-primary-foreground',
                  isActive && 'border-primary text-primary bg-primary/10',
                  !isActive && !isComplete && 'border-muted-foreground/30 text-muted-foreground/50',
                  isClickable && 'cursor-pointer hover:scale-110 transition-transform',
                  !isClickable && 'cursor-default'
                )}
              >
                {isComplete ? <Check className="w-4 h-4" /> : stepNum}
              </button>
              <span
                className={cn(
                  'text-[10px] mt-1 text-center leading-tight',
                  isActive ? 'text-primary font-medium' : 'text-muted-foreground/60'
                )}
              >
                <span className="hidden sm:inline">{step.label}</span>
                <span className="sm:hidden">{step.shortLabel}</span>
              </span>
            </div>
            {index < steps.length - 1 && (
              <div
                className={cn(
                  'h-0.5 flex-1 mx-1 mt-[-16px]',
                  isComplete ? 'bg-primary' : 'bg-muted-foreground/20'
                )}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}
